import React from 'react';
import { observer } from 'mobx-react-lite';

import zipObject from 'lodash/zipObject';
import max from 'lodash/max';
import mean from 'lodash/mean';
import sum from 'lodash/sum';
import omit from 'lodash/omit';

import eachDay from 'date-fns/each_day';
import format from 'date-fns/format';
import startOfDay from 'date-fns/start_of_day';

import { BarChart, Label, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';

import { ChartColours } from '../../ChartColours';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { Job } from 'models';
import { formatMinutes } from './helpers';

const CustomTooltip = (props: any) => {
    if (props.payload && props.payload[0] != null) {
        const newPayload = [
            {
                color: ChartColours[props.payload.length % ChartColours.length],
                dataKey: 'Total Job Duration',
                name: 'Total Job Duration',
                value: formatMinutes(
                    `${sum(
                        Object.values(
                            omit(props.payload[0].payload, ['Jobs Completed', 'date'])
                        ).map((v) => Number(v))
                    )}`
                )
            },
            ...props.payload
                .slice()
                .reverse()
                .map((p: any) => ({
                    ...p,
                    value: formatMinutes(p.value)
                }))
        ];

        return (
            <DefaultTooltipContent
                {...props}
                payload={newPayload}
                labelFormatter={(label: number) => format(new Date(label), 'dddd Do MMMM YYYY')}
            />
        );
    }
    return <DefaultTooltipContent {...props} />;
};

interface JobsDurationChartProps {
    jobs: Job[];
    startDate: Date;
    endDate: Date;
}

export default observer<JobsDurationChartProps>(function JobsDurationChart({
    jobs,
    startDate,
    endDate
}) {
    const generateTaskNames = (length: number) => {
        if (length === undefined || length === 0) {
            return [];
        } else {
            const res = [...Array(length - 1)].map((_, i) => `Task ${i + 1}`);
            res.push('Completion');
            return res;
        }
    };

    const timesByDate = jobs
        .filter(
            (j) =>
                j.dispatches!.items.find((d) => d.type === 'underway') &&
                j.status == 'completed' &&
                j.tasks.items.every((t) => t.completed?.time || 0 < j.finished!)
        )
        .map((j) => {
            const jobStartTime =
                j.dispatches!.items.find((d) => d.type === 'underway')!.created || 0;
            const jobTasks = j.tasks.items.filter((t) => t.completed);

            const taskTimes = jobTasks.map((t, i) => {
                const completedTime = t.completed!.time!;
                if (i === 0) {
                    return completedTime - jobStartTime;
                } else {
                    return completedTime - jobTasks[i - 1]!.completed!.time!;
                }
            });

            if (taskTimes.length) {
                taskTimes.push(j.finished! - jobTasks.slice(-1)[0]!.completed!.time!);
            }

            return {
                date: startOfDay(j.finished!).getTime(),
                times: taskTimes
            };
        });

    const dates = eachDay(startDate, endDate);

    const data = dates.map((d) => {
        const timeAverages = timesByDate
            .filter((t) => {
                return t.date === d.getTime();
            })
            .map((t) => t.times)
            .reduce((totals, times) => {
                times.forEach((time, i) => {
                    if (totals[i]) {
                        totals[i]!.push(time);
                    } else {
                        totals[i] = [time];
                    }
                });

                return totals;
            }, [] as number[][])
            .map((times) => (mean(times) / (1000 * 60)).toFixed(2));

        return {
            date: format(d, 'D MMM YY'),
            ...zipObject(generateTaskNames(timeAverages.length), timeAverages)
        };
    });

    const maxTasks = max(timesByDate.map((d) => d.times.length)) || 0;

    return (
        <ObservableAutoSizer customChildren={({ width }: { width: number }) => (
            <BarChart
                width={width}
                height={220}
                data={data}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                }}>
                <CartesianGrid strokeDasharray="3 5" />
                <XAxis
                    dataKey="date"
                    minTickGap={100}
                    tickFormatter={(t: number) => format(new Date(t), 'Do MMM')}
                />
                <YAxis>
                    <Label
                        value={'Time (mins)'}
                        angle={-90}
                        position={'insideLeft'}
                        offset={15}
                        style={{ textAnchor: 'middle', fontSize: '11px' }}
                    />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                {generateTaskNames(maxTasks).map((task, i) => (
                    <Bar
                        dataKey={task}
                        key={task}
                        stackId="a"
                        fill={ChartColours[i % ChartColours.length]}
                    />
                ))}
            </BarChart>
        )} />
    );
});
